import AdminDashboard from "@/pages/admin/dashboard";
import { AdminErrors, AdminActivity } from "@/pages/admin/operations";
import { useState, useEffect, useCallback, createContext, useContext } from "react";
import * as Sentry from "@sentry/react";
import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { systemNotifications } from "@/lib/system-notifications";
import { SystemNotifications } from "@/components/ui/system-notifications";
import { Toaster } from "@/components/ui/toaster";
import { useCanvasStore } from "@/pages/canvas/canvas-store";
import { toast } from "@/hooks/use-toast";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import AdDetails from "@/pages/ad-details";
import Privacy from "@/pages/privacy";
import Terms from "@/pages/terms";
import BoardsPage from "@/pages/boards";
import PredictionBrain from "@/pages/admin/prediction-brain";
import RubricEditor from "@/pages/admin/rubric-editor";
import CalibrationLibrary from "@/pages/admin/calibration-library";
import PlatformDocs from "@/pages/admin/platform-docs";
import WinningAds from "@/pages/admin/winning-ads";
import AdLibrarySeederPage from "@/pages/admin/ad-library-seeder";
import AdminAdTemplatesPage from "@/pages/admin/ad-templates";
import TalentLibraryPage from "@/pages/admin/talent-library";
import AdminPricingPage from "@/pages/admin/pricing";
import AdminCouponsPage from "@/pages/admin/coupons";
import AdminUsersPage from "@/pages/admin/users";
import AdminMetaIntelPage from "@/pages/admin/meta-intel";
import ConnectedAccountsPage from "@/pages/admin/connected-accounts";
import { ComingSoonPage } from "@/pages/admin/coming-soon";
import { AdminLayout } from "@/layouts/AdminLayout";
import MetaConnections from "@/pages/meta-connections";
import MetaCampaigns from "@/pages/meta-campaigns";
import MetaInsights from "@/pages/meta-insights";
import MetaCampaignDetail from "@/pages/meta-campaign-detail";
import MockData from "@/pages/mock-data";
import Login from "@/pages/login";
import Signup from "@/pages/signup";
import ForgotPassword from "@/pages/forgot-password";
import ResetPassword from "@/pages/reset-password";
import VerifyEmail from "@/pages/verify-email";
import { VerifyEmailBanner } from "@/components/VerifyEmailBanner";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import BillingSelectPlan from "@/pages/billing-select-plan";
import OnboardingWizard from "@/pages/onboarding-wizard";
import Profile from "@/pages/profile";
import Documentation from "@/pages/documentation";
import { WORKSPACE_ROUTE_PATTERN } from "@/pages/canvas/workspace-routes";
import { CanvasPage } from "@/pages/canvas/canvas-page";
import { MetaAccountProvider } from "@/contexts/MetaAccountContext";
import { DashboardAccountProvider, useDashboardAccounts } from "@/contexts/DashboardAccountContext";
import { DateRangeProvider } from "@/contexts/DateRangeContext";
import { CreditsGateProvider } from "./lib/credits-gate";
import { Loader2 } from "lucide-react";

interface AuthUser {
  id: number;
  email: string;
  name: string;
  timezone: string | null;
  isAdmin?: boolean;
  emailVerified?: boolean;
  needsEmailVerification?: boolean;
  hasActiveSubscription?: boolean;
  /** v7.46 — card-free promo access (/promosignup). Has a sub so the paywall
   *  passes, but never paid — so they may still reach the plan picker. */
  isPromoAccess?: boolean;
  /** v7.06 — true only when billing is satisfied AND the first-run wizard has
   *  never been completed or skipped. Drives the full-screen wizard below. */
  needsOnboarding?: boolean;
  /** v7.44 — in-app first-run tour already finished or skipped. */
  tourCompleted?: boolean;
  /** v7.10 — resolved plan entitlements (brand cap + video access). videoGa
   *  tells the UI which refusal copy applies pre/post video GA. */
  entitlements?: {
    planName: string | null;
    maxBrands: number | null;
    videoAccess: boolean;
    videoGa: boolean;
  };
  subscription?: {
    planId: number;
    status: string;
    renewsAt: string;
    cachedBalance: number;
  } | null;
}

const UserContext = createContext<AuthUser | null>(null);
export function useAuthUser(): AuthUser | null {
  return useContext(UserContext);
}

function AppRouter({ user, onLogout, onUserUpdate }: { user: AuthUser; onLogout: () => void; onUserUpdate: () => void }) {
  return (
    <Switch>
      <Route path={WORKSPACE_ROUTE_PATTERN} component={CanvasPage} />
      <Route path="/dashboard">{() => <Redirect to="/analytics" />}</Route>
      <Route path="/find-ads">{() => <Redirect to="/discover" />}</Route>
      <Route path="/boards/:id" component={BoardsPage} />
      <Route path="/boards" component={BoardsPage} />
      <Route path="/ad/:id" component={AdDetails} />
      <Route path="/creative/:id/edit">{() => <Redirect to="/canvas" />}</Route>
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms" component={Terms} />
      <Route path="/organic-posts">{() => <Redirect to="/canvas" />}</Route>
      <Route path="/competitor-analysis/:boardId">{() => <Redirect to="/brands" />}</Route>
      <Route path="/competitor-analysis">{() => <Redirect to="/brands" />}</Route>
      <Route path="/askmoji">{() => <Redirect to="/canvas" />}</Route>
      <Route path="/docs">{() => <Redirect to="/documentation" />}</Route>
      <Route path="/documentation" component={Documentation} />
      <Route path="/meta/connections" component={MetaConnections} />
      <Route path="/meta/dashboard">{() => <Redirect to="/dashboard" />}</Route>
      <Route path="/meta/campaigns/:campaignId">{(params) => <MetaCampaignDetail campaignId={params.campaignId} />}</Route>
      <Route path="/meta/campaigns" component={MetaCampaigns} />
      <Route path="/meta/insights" component={MetaInsights} />
      {/* Demo/mock tooling is admin-only — regular users get bounced to canvas */}
      <Route path="/mock-data">{() => user.isAdmin ? <MockData /> : <Redirect to="/canvas" />}</Route>
      <Route path="/profile">{() => <Profile user={user} onLogout={onLogout} onUserUpdate={onUserUpdate} />}</Route>
      <Route path="/admin">{() => <Redirect to="/admin/dashboard" />}</Route>
      <Route path="/admin/:rest*">{() => (
        <AdminLayout>
          <Switch>
            <Route path="/admin/dashboard" component={AdminDashboard} />
            <Route path="/admin/errors" component={AdminErrors} />
            <Route path="/admin/activity" component={AdminActivity} />
            <Route path="/admin/brain" component={PredictionBrain} />
            <Route path="/admin/rubric" component={RubricEditor} />
            <Route path="/admin/calibration" component={CalibrationLibrary} />
            <Route path="/admin/platform-docs" component={PlatformDocs} />
            <Route path="/admin/competitors">{() => <WinningAds key="competitors" initialTab="competitor" />}</Route>
            <Route path="/admin/winning-ads">{() => <WinningAds key="winning-ads" />}</Route>
            <Route path="/admin/ad-library-seeder" component={AdLibrarySeederPage} />
            <Route path="/admin/ad-templates" component={AdminAdTemplatesPage} />
            <Route path="/admin/talent-library" component={TalentLibraryPage} />
            <Route path="/admin/pricing" component={AdminPricingPage} />
            <Route path="/admin/coupons" component={AdminCouponsPage} />
            <Route path="/admin/accuracy">{() => <ComingSoonPage title="Accuracy Tracker" description="Track rubric prediction accuracy over time across all platforms and verticals." />}</Route>
            <Route path="/admin/simulations">{() => <ComingSoonPage title="Simulation History" description="Browse historical ad scoring simulations and compare rubric performance." />}</Route>
            <Route path="/admin/usage">{() => <ComingSoonPage title="Usage Analytics" description="Monitor platform usage, AI calls, and resource consumption." />}</Route>
            <Route path="/admin/accounts" component={ConnectedAccountsPage} />
            <Route path="/admin/users" component={AdminUsersPage} />
            <Route path="/admin/meta-intel" component={AdminMetaIntelPage} />
            <Route path="/admin/api-keys">{() => <ComingSoonPage title="API Keys" description="Manage API credentials for all integrated platforms." />}</Route>
            <Route path="/admin/settings">{() => <ComingSoonPage title="System Settings" description="Configure global system settings for the Admoji platform." />}</Route>
            <Route path="/admin">{() => <Redirect to="/admin/dashboard" />}</Route>
          </Switch>
        </AdminLayout>
      )}</Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  const [, setLocation] = useLocation();

  useEffect(() => {
    setLocation("/login");
  }, [setLocation]);

  return <>{children}</>;
}

let tzBootstrapDone = false;
function TimezoneBootstrap({ onUpdate }: { onUpdate: () => void }) {
  const user = useAuthUser();
  const { allAccounts } = useDashboardAccounts();

  useEffect(() => {
    if (tzBootstrapDone) return;
    if (user && !user.timezone && allAccounts.length > 0 && allAccounts[0].timezone) {
      tzBootstrapDone = true;
      fetch("/api/auth/timezone", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ timezone: allAccounts[0].timezone }),
      })
        .then(r => { if (r.ok) onUpdate(); })
        .catch(() => { tzBootstrapDone = false; });
    }
  }, [user, allAccounts, onUpdate]);

  return null;
}

function App() {
  // v7.44 — the first-run tour hides the verify-email banner while it runs.
  const tourActive = useCanvasStore((st) => st.tourActive);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(true);
  const [location, setLocation] = useLocation();
  // When the user lands back from Stripe Checkout we run an async verify-
  // session call (~5s on cold cache). Initialize this flag from the URL so
  // the paywall guard doesn't fire BEFORE verify-session can flip
  // hasActiveSubscription — otherwise the user bounces to /billing/select-plan
  // mid-verification.
  const [verifyingStripe, setVerifyingStripe] = useState(() => {
    if (typeof window === "undefined") return false;
    const p = new URLSearchParams(window.location.search);
    return p.get("stripe") === "success" && !!p.get("session_id");
  });

  const checkAuth = useCallback(async () => {
    setChecking(true);
    try {
      const res = await fetch("/api/auth/me", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        // v3.82 — tag every Sentry event with WHO hit it, so errors are
        // filterable by user. No-op when VITE_SENTRY_DSN is unset.
        try { Sentry.setUser({ id: String(data.id), email: data.email }); } catch { /* sentry optional */ }
      } else {
        setUser(null);
        try { Sentry.setUser(null); } catch { /* sentry optional */ }
      }
    } catch {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Stripe Checkout return handler.
  //
  // When Stripe redirects back to /?stripe=success&session_id=cs_test_..., we
  // POST the session id to /api/billing/verify-session. The server fetches the
  // session from Stripe, confirms paid + ours, and runs assignPlanToUser. Then
  // we re-fetch /api/auth/me so hasActiveSubscription flips true and the app
  // unlocks. This is the local-dev fallback that works without the webhook
  // tunnel; in prod the webhook handler typically beats us to the punch and
  // the verify call is a no-op via idempotency check.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe") !== "success") return;
    const sessionId = params.get("session_id");
    if (!sessionId) return;

    (async () => {
      try {
        await fetch("/api/billing/verify-session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ sessionId }),
        });
      } catch (err) {
        console.error("[Stripe] verify-session failed:", err);
      } finally {
        // Strip the query params so a refresh doesn't re-verify forever
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, "", cleanUrl);
        // Re-fetch user to pick up the new active subscription
        await checkAuth();
        // Release the paywall gate now that hasActiveSubscription has flipped.
        setVerifyingStripe(false);
      }
    })();
  }, [checkAuth]);

  // Customer Portal return handler.
  //
  // Stripe's portal redirects back to /?billing=updated after the user changes
  // plan there. We POST /api/billing/reconcile, which pulls their current
  // subscription from Stripe and updates our DB (plan + credits) — so a portal
  // switch reflects immediately WITHOUT needing a webhook. Then we refresh the
  // cached balance + auth so the new plan shows everywhere.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing") !== "updated") return;

    (async () => {
      try {
        await fetch("/api/billing/reconcile", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
      } catch (err) {
        console.error("[Stripe] reconcile failed:", err);
      } finally {
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, "", cleanUrl);
        queryClient.invalidateQueries({ queryKey: ["/api/usage/balance"] });
        await checkAuth();
      }
    })();
  }, [checkAuth]);

  // Credit top-up return handler.
  //
  // Stripe Checkout (one-time payment) redirects back to
  // /?topup=success&session_id=cs_... We POST /api/billing/verify-topup, which
  // confirms the payment and grants the credits (idempotent), then refresh the
  // cached balance so the new total shows immediately.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("topup") !== "success") return;
    const sessionId = params.get("session_id");
    if (!sessionId) return;

    (async () => {
      try {
        const res = await fetch("/api/billing/verify-topup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ sessionId }),
        });
        const data = await res.json().catch(() => ({}));
        if (data?.ok && data?.credits) {
          toast({
            title: "Credits added",
            description: `${Number(data.credits).toLocaleString()} credits added to your balance.`,
          });
        }
      } catch (err) {
        console.error("[Stripe] verify-topup failed:", err);
      } finally {
        window.history.replaceState({}, "", window.location.pathname);
        queryClient.invalidateQueries({ queryKey: ["/api/usage/balance"] });
      }
    })();
  }, []);

  // Public routes that unauthenticated users can visit. Anything else bounces
  // them to /login. /signup is the new public route added with paywall flow.
  const PUBLIC_ROUTES = ["/login", "/signup", "/promosignup", "/forgot-password", "/reset-password", "/verify-email", "/documentation", "/privacy", "/terms"];

  useEffect(() => {
    if (!checking && !user && !PUBLIC_ROUTES.includes(location)) {
      setLocation("/login");
    }
    if (!checking && user && location === "/login") {
      setLocation("/");
    }
    if (!checking && user && location === "/signup") {
      setLocation("/");
    }
  }, [checking, user, location, setLocation]);

  const handleLogout = useCallback(() => {
    systemNotifications.clear();
    setUser(null);
    queryClient.clear();
    setLocation("/login");
  }, [setLocation]);

  if (checking || verifyingStripe) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center flex-col gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        {verifyingStripe && (
          <div className="text-sm text-neutral-600">Finalizing your subscription…</div>
        )}
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <UserContext.Provider value={user}>
          <MetaAccountProvider>
            <DashboardAccountProvider>
              <DateRangeProvider>
                <CreditsGateProvider>
                  <TimezoneBootstrap onUpdate={checkAuth} />
                  <Toaster />
                  <SystemNotifications />
                  <ImpersonationBanner />
                  {/* Verify-email soft nag shows only INSIDE the main app —
                      never on the paywall/plan picker a fresh signup lands on
                      (Dan: "we dont need to show the email verification on
                      this page on first sign in, only on main"). */}
                  {/* v7.06 — also suppressed while the first-run wizard owns
                      the screen: the wizard runs AFTER checkout, so
                      hasActiveSubscription is true by then and the banner
                      would otherwise float over a full-screen surface. */}
                  <VerifyEmailBanner
                    show={Boolean(
                      user?.needsEmailVerification &&
                        (user?.hasActiveSubscription || user?.isAdmin) &&
                        !user?.needsOnboarding &&
                        !tourActive,
                    )}
                    email={user?.email}
                  />
                  <Switch>
                    <Route path="/login">
                      {user ? <Redirect to="/" /> : <Login onLogin={checkAuth} />}
                    </Route>
                    <Route path="/signup">
                      {user ? <Redirect to="/" /> : <Signup onLogin={checkAuth} />}
                    </Route>
                    {/* v7.46 — unlisted card-free signup for influencers/promo
                        users. Same form, coupon REQUIRED, no trial/card step. */}
                    <Route path="/promosignup">
                      {user ? <Redirect to="/" /> : <Signup onLogin={checkAuth} variant="promo" />}
                    </Route>
                    <Route path="/billing/select-plan">
                      {!user
                        ? <Redirect to="/login" />
                        : (user.hasActiveSubscription && !user.isPromoAccess)
                          ? <Redirect to="/" />
                          : <BillingSelectPlan />}
                    </Route>
                    <Route path="/documentation" component={Documentation} />
                    {/* Legal pages must render for EVERYONE — Meta's reviewer
                        visits these logged-out. The copies inside AppRouter
                        never render for unauthenticated visitors (they fell
                        through to the login screen); these top-level routes
                        match first for both auth states. */}
                    <Route path="/privacy" component={Privacy} />
                    <Route path="/terms" component={Terms} />
                    {/* Auth-email flows — render for everyone (a logged-in but
                        unverified user must be able to open the verify link, and
                        a logged-out user must reach forgot/reset). */}
                    <Route path="/forgot-password" component={ForgotPassword} />
                    <Route path="/reset-password" component={ResetPassword} />
                    <Route path="/verify-email" component={VerifyEmail} />
                    {user ? (
                      // Paywall gate: any logged-in user without an active subscription
                      // (and who isn't an admin) gets bounced to the plan picker. Admins
                      // bypass — they're internal and shouldn't be blocked by billing.
                      !user.hasActiveSubscription && !user.isAdmin ? (
                        <Redirect to="/billing/select-plan" />
                      ) : user.needsOnboarding ? (
                        // v7.06 — FIRST-RUN WIZARD. Rendered here, between the
                        // paywall and AppRouter, so it is genuinely full-screen
                        // (anything inside AppRouter inherits the canvas
                        // chrome) and so it can only ever appear AFTER billing
                        // is satisfied. onDone re-runs checkAuth, which clears
                        // needsOnboarding and falls through to the app.
                        <OnboardingWizard onDone={checkAuth} />
                      ) : (
                        <AppRouter user={user} onLogout={handleLogout} onUserUpdate={checkAuth} />
                      )
                    ) : (
                      <AuthGuard>
                        <Login onLogin={checkAuth} />
                      </AuthGuard>
                    )}
                  </Switch>
                </CreditsGateProvider>
              </DateRangeProvider>
            </DashboardAccountProvider>
          </MetaAccountProvider>
        </UserContext.Provider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
